import Icon from '@mdi/react';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  pdsTv,
} from '@nestle-npus-nbm/pds-ui';
import * as React from 'react';
import { mdiChevronDown } from 'src/assets/icons/mdiIcons';
import { CMS_THEME } from 'src/common/enums';
import { Link } from 'src/components/Link';
import { generateButtonClassName } from 'src/components/button/CmsButton';

interface NavItem {
  id: string | number;
  label: string;
  href: string;
}

interface NavbarProps {
  title: string;
  items: NavItem[];
  theme: CMS_THEME;
  handleGA: (title: string) => void;
}

const styles = pdsTv({
  slots: {
    contentContainer: '',
  },
  variants: {
    theme: {
      light: {
        contentContainer: 'pds-bg-surface',
      },
      dark: {
        contentContainer: 'pds-bg-neutral',
      },
      neutral: {
        contentContainer: '',
      },
    },
  },
});

const SubnavMenuMobile = (props: NavbarProps) => {
  const { items, title, theme, handleGA } = props;

  const { contentContainer } = React.useMemo(() => styles({ theme }), [theme]);

  const renderMenu = (item: NavItem) => {
    return (
      <React.Fragment key={item.id}>
        <NavigationMenuItem>
          <NavigationMenuLink asChild className="pds-py-3 pds-transition-colors">
            <div>
              <Link
                theme={theme}
                analytics={false}
                to={item.href}
                onClick={() => handleGA(item.label)}
                partiallyActive
                className={`hover:underline pds-relative pds-block pds-max-w-max pds-text-title-sm pds-font-semibold pds-no-underline [--tw-content:''] after:pds-animated-underline aria-[current=page]:pds-font-extrabold aria-[current=page]:after:pds-h-3 aria-[current=page]:after:pds-w-full ${
                  theme === CMS_THEME.LIGHT
                    ? 'aria-[current=page]:after:pds-bg-surface-text'
                    : 'aria-[current=page]:after:pds-bg-neutral-text'
                }`}
              >
                {item.label}
              </Link>
            </div>
          </NavigationMenuLink>
        </NavigationMenuItem>
      </React.Fragment>
    );
  };
  const triggerStyles = generateButtonClassName({
    cmsTheme: theme === CMS_THEME.LIGHT ? CMS_THEME.DARK : CMS_THEME.LIGHT,
    width: 'full',
    variant: 'btn',
    className: [
      'pds-flex pds-h-[68px] pds-w-full pds-grow pds-justify-between pds-text-title-sm pds-rounded-none [&>svg:last-of-type]:pds-hidden data-[active]:pds-bg-[inherit] data-[state=open]:pds-bg-[inherit] focus:pds-bg-[inherit]',
      `${
        theme === CMS_THEME.LIGHT
          ? 'data-[state=open]:pds-text-surface-text data-[state=open]:pds-bg-surface-layer focus:pds-text-surface-text focus:pds-bg-surface-layer'
          : 'data-[state=open]:pds-text-neutral-text data-[state=open]:pds-bg-neutral-layer focus:pds-text-neutral-text focus:pds-bg-neutral-layer'
      }`,
    ],
  });
  return (
    <NavigationMenu
      aria-label={title}
      className={contentContainer({
        class:
          'pds-z-[10] pds-flex pds-max-w-full [&_>_*_div]:pds-mt-0 [&_>_*_div]:pds-rounded-none [&_>_div]:!pds-w-full',
      })}
    >
      <NavigationMenuList asChild className={contentContainer({ class: 'pds-block' })}>
        <div>
          <NavigationMenuItem asChild className={contentContainer({ class: 'pds-block' })}>
            <div>
              <NavigationMenuTrigger className={triggerStyles} aria-haspopup="menu">
                {title}
                <Icon
                  path={mdiChevronDown}
                  size={1.5}
                  className="pds-relative pds-top-[1px] pds-h-5 pds-w-5 pds-transition pds-duration-300 group-data-[state=open]:pds-rotate-180"
                  aria-hidden="true"
                />
              </NavigationMenuTrigger>
              <NavigationMenuContent
                forceMount
                className={contentContainer({
                  class:
                    'pds-space-y-4 pds-px-4 pds-pb-4.5 pds-pt-0 pds-shadow-stronger data-[state=closed]:pds-hidden',
                })}
              >
                <ul className="pds-overflow-clip pds-rounded pds-p-0">{items.map(renderMenu)}</ul>
              </NavigationMenuContent>
            </div>
          </NavigationMenuItem>
        </div>
      </NavigationMenuList>

      <NavigationMenuViewport />
    </NavigationMenu>
  );
};

export default SubnavMenuMobile;
